import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Loader2,
  MessageSquare,
  Paperclip,
  Plus,
  RefreshCw,
  ScanLine,
  Send,
  Sparkles,
  Square,
  X,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  type AttachmentKind,
  CHAT_INSTRUCTION_MAX_CHARS,
} from '@/api/attachments'
import { API_BASE_URL, AuthError } from '@/api/client'
import { captureSafeException, captureSafeMessage } from '@/lib/sentry-instrument'
import {
  type ChatContextSummary,
  type ChatMessageEvent,
  type ChatSessionSummary,
  type DocumentDetail,
  type EditProposalRead,
  type ErrorEvent as DocfillerErrorEvent,
  type FieldProposalEvent,
  type SavedPromptRead,
} from '@/api/docfiller-types'
import {
  applyFieldPatch,
  queryKeys,
  useAcceptProposal,
  useChat,
  useDocumentChats,
  useRejectProposal,
  useSendChatMessage,
  useTriggerFill,
} from '@/api/queries'
import { subscribeSSE } from '@/api/sse'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  AttachmentComposer,
  AttachmentDropOverlay,
  isReady as isAttachmentReady,
  useAttachmentDropTarget,
  useChatAttachments,
} from './attachments'
import { EffectiveInstructionsPanel } from './EffectiveInstructionsPanel'
import { SavedPromptPicker } from './SavedPromptPicker'
import {
  appendChatEntry,
  countProposalsForRun,
  formatChatTimestamp,
  formatConfidence,
  fullChatTimestamp,
  humanizeValue,
  normalizeProposal,
  proposalCitations,
  sanitizeAssistantText,
  shortRunId,
  tryParseToolCall,
  type ChatEntry,
  type CitationSource,
  type NormalizedProposal,
  type SentAttachmentSummary,
} from './chat-utils'
import { formatByteSize, kindLabel } from './attachments/format'

const EXAMPLE_PROMPTS = [
  'Fill the form using sensible defaults.',
  'Fill name, email, and phone. Leave date fields blank.',
] as const

// Confidence below this triggers a visible "Low confidence" tag on a
// proposal. Higher proposals keep the percent hidden in default mode and
// only expose it under the debug disclosure — it's noise when the agent
// is confident, which is the vast majority of the time.
const LOW_CONFIDENCE_THRESHOLD = 0.6

type ChatMode = 'fill' | 'chat'

export function ChatPanel({
  doc,
  readOnly = false,
}: {
  doc: DocumentDetail
  readOnly?: boolean
}) {
  const qc = useQueryClient()
  const triggerFill = useTriggerFill(doc.uuid)
  const sendChatMessage = useSendChatMessage(doc.uuid)
  const chats = useDocumentChats(doc.uuid)
  const attachments = useChatAttachments()
  const [entries, setEntries] = useState<ChatEntry[]>([])
  const [input, setInput] = useState('')
  const [running, setRunning] = useState(false)
  const [mode, setMode] = useState<ChatMode>('fill')
  const [chatSessionUuid, setChatSessionUuid] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(true)
  const [historySessionUuid, setHistorySessionUuid] = useState<string | null>(null)
  // Picked saved prompt to layer into the next fill run. Cleared back to
  // ``null`` after each successful send so the picker doesn't silently
  // tag a follow-up run the user didn't intend.
  const [selectedPrompt, setSelectedPrompt] = useState<SavedPromptRead | null>(
    null,
  )
  const controllerRef = useRef<AbortController | null>(null)
  const listEndRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Close the SSE stream on unmount. Doc-id changes remount the panel via
    // the parent's `key={doc.uuid}`, so we don't need a sync reset effect.
    return () => {
      controllerRef.current?.abort()
      controllerRef.current = null
    }
  }, [])

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [entries])

  const append = useCallback(
    (entry: ChatEntry) => setEntries((prev) => appendChatEntry(prev, entry)),
    [],
  )

  const updateProposal = (uuid: string, updated: EditProposalRead) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.kind === 'pending-proposal' && e.proposal.uuid === uuid
          ? { ...e, proposal: updated }
          : e,
      ),
    )
  }

  const stop = () => {
    controllerRef.current?.abort()
    controllerRef.current = null
    setRunning(false)
    append({ kind: 'run-stopped', at: new Date() })
  }

  const startFillStream = useCallback(
    (runId: string | null = null) => {
      if (controllerRef.current) return
      setRunning(true)

      const ctl = new AbortController()
      controllerRef.current = ctl
      const url = `${API_BASE_URL}/dashboard/documents/${doc.uuid}/fill/stream`
      void subscribeSSE(url, {
        signal: ctl.signal,
        onEvent: (event) => {
          let parsed: unknown
          try {
            parsed = JSON.parse(event.data)
          } catch {
            parsed = null
          }
          if (event.event === 'field_proposal' && parsed) {
            const p = parsed as FieldProposalEvent
            const normalized = normalizeProposal(p, doc.fields)
            append({ kind: 'proposal', proposal: normalized, at: new Date() })
            toast.success(
              normalized.applied
                ? `Filled ${normalized.displayName}`
                : `Suggested ${normalized.displayName}`,
            )
            // Optimistically reflect the proposal in the cached doc so the
            // Fields tab updates the moment the SSE event lands — without
            // it the user would wait for the next 2s detail-poll. The poll
            // still runs and reconciles against the server.
            if (p.field_uuid) {
              const fieldUuid = p.field_uuid
              qc.setQueryData<DocumentDetail | undefined>(
                queryKeys.documents.detail(doc.uuid),
                (prev) =>
                  prev
                    ? {
                        ...prev,
                        fields: prev.fields.map((f) =>
                          f.uuid === fieldUuid
                            ? applyFieldPatch(
                                f,
                                p.applied
                                  ? {
                                      current_value: p.value,
                                      accepted_by: 'agent',
                                      clear_suggested: true,
                                    }
                                  : { suggested_value: p.value },
                              )
                            : f,
                        ),
                      }
                    : prev,
              )
            } else {
              qc.invalidateQueries({
                queryKey: queryKeys.documents.detail(doc.uuid),
              })
            }
          } else if (event.event === 'message' && parsed) {
            const m = parsed as ChatMessageEvent
            const role = m.role === 'user' ? 'user' : 'assistant'
            const content =
              role === 'assistant' ? sanitizeAssistantText(m.content) : m.content
            if (content == null) return
            const tool = role === 'assistant' ? tryParseToolCall(content) : null
            if (tool) {
              append({ kind: 'tool', ...tool, at: new Date() })
            } else {
              append({ kind: role, content, at: new Date() })
            }
          } else if (event.event === 'done') {
            append({ kind: 'run-done', at: new Date() })
            setRunning(false)
            ctl.abort()
            controllerRef.current = null
            qc.invalidateQueries({
              queryKey: queryKeys.documents.detail(doc.uuid),
            })
          } else if (event.event === 'error') {
            const err =
              (parsed as DocfillerErrorEvent | null)?.message ?? 'agent error'
            append({ kind: 'error', content: err, at: new Date() })
            toast.error(err)
            setRunning(false)
            ctl.abort()
            controllerRef.current = null
            qc.invalidateQueries({
              queryKey: queryKeys.documents.detail(doc.uuid),
            })
            // SSE error event — the backend already redacts upstream provider
            // bodies before emitting this, but we still scrub the message
            // through the helper before adding it as an extra. We tag the
            // operation + run_id + doc_uuid so on-call can pivot from a
            // Sentry issue to the matching ``fill.failed`` backend log line.
            captureSafeMessage('fill.stream_error_event', 'error', {
              operation: 'fill_stream',
              route: '/dashboard/documents/{uuid}/fill/stream',
              doc_uuid: doc.uuid,
              run_id: runId ?? 'unknown',
            })
          }
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : String(err)
          append({ kind: 'error', content: `Stream error: ${msg}`, at: new Date() })
          setRunning(false)
          controllerRef.current = null
          qc.invalidateQueries({
            queryKey: queryKeys.documents.detail(doc.uuid),
          })
          // Network-level SSE failure (connection drop, parse error, etc.).
          // Captured as exception so Sentry groups by stack trace and so the
          // error type carries forward to the dashboard.
          captureSafeException(err, 'fill.stream_failed', {
            operation: 'fill_stream',
            route: '/dashboard/documents/{uuid}/fill/stream',
            doc_uuid: doc.uuid,
            run_id: runId ?? 'unknown',
          })
        },
      })
    },
    [append, doc.fields, doc.uuid, qc],
  )

  const sendFill = async (
    text: string,
    contextFiles: { r2_key: string }[],
    sentAttachmentSummaries: SentAttachmentSummary[],
    savedPromptUuid: string | null = null,
  ) => {
    const res = await triggerFill.mutateAsync({
      instructions: text,
      context_files: contextFiles.length > 0 ? contextFiles : null,
      saved_prompt_uuid: savedPromptUuid,
    })
    // Only claim the context was sent once the start-agent call succeeds.
    // On failure we leave the chip rail intact so the user can retry
    // without re-attaching anything.
    if (sentAttachmentSummaries.length > 0) {
      append({ kind: 'attachments', attachments: sentAttachmentSummaries, at: new Date() })
    }
    attachments.reset()
    append({ kind: 'run-start', runId: res.run_id, at: new Date() })
    startFillStream(res.run_id)
  }

  const sendChat = async (
    text: string,
    contextFiles: { r2_key: string }[],
    sentAttachmentSummaries: SentAttachmentSummary[],
  ) => {
    setRunning(true)
    const res = await sendChatMessage.mutateAsync({
      message: text,
      chat_session_uuid: chatSessionUuid,
      context_files: contextFiles.length > 0 ? contextFiles : null,
    })
    if (sentAttachmentSummaries.length > 0) {
      append({ kind: 'attachments', attachments: sentAttachmentSummaries, at: new Date() })
    }
    attachments.reset()
    setChatSessionUuid(res.chat_session_uuid)
    setRunning(false)

    if (res.assistant_text.trim()) {
      append({ kind: 'assistant', content: res.assistant_text, at: new Date() })
    }
    append({ kind: 'context-used', summary: res.context, at: new Date() })
    for (const proposal of res.proposals) {
      append({ kind: 'pending-proposal', proposal, at: new Date() })
    }
  }

  const sendPrompt = async (text: string) => {
    if (readOnly || !text || running) return
    if (attachments.uploading) {
      toast.message('Waiting for attachments to finish uploading…')
      return
    }
    if (attachments.attachments.some((a) => a.status === 'error')) {
      toast.error('Resolve failed attachments before sending.')
      return
    }
    // Mirror the backend's MAX_INSTRUCTION_CHARS gate so the user gets an
    // instant refusal instead of a 413 after the round-trip. The backend
    // is still the authoritative check — this is just UX, not security.
    if (text.length > CHAT_INSTRUCTION_MAX_CHARS) {
      toast.error(
        `Message is too long (${text.length.toLocaleString()} / ${CHAT_INSTRUCTION_MAX_CHARS.toLocaleString()} chars). Trim and try again.`,
      )
      return
    }
    const ready = attachments.readyAttachments.filter(isAttachmentReady)
    const contextFiles = ready.map((a) => ({ r2_key: a.server.r2_key }))
    const sentAttachmentSummaries: SentAttachmentSummary[] = ready.map((a) => ({
      uuid: a.server.uuid,
      filename: a.server.filename,
      kind: a.server.kind,
      byteSize: a.server.byte_size,
    }))
    setShowHistory(false)
    setHistorySessionUuid(null)
    setInput('')
    append({ kind: 'user', content: text, at: new Date() })

    try {
      if (mode === 'chat') {
        await sendChat(text, contextFiles, sentAttachmentSummaries)
      } else {
        await sendFill(
          text,
          contextFiles,
          sentAttachmentSummaries,
          selectedPrompt?.uuid ?? null,
        )
        // Clear the picker so the next send doesn't silently reapply the
        // prompt. Persisting it across runs is a footgun — the user has to
        // opt-in again if they want the same prompt for a follow-up.
        setSelectedPrompt(null)
      }
    } catch (err) {
      // ``ApiError`` / ``AuthError`` already carry the (server-redacted)
      // ``detail`` string in ``.message``. The backend strips raw provider
      // bodies before they get here, so it's safe to display verbatim; for
      // anything else (network drop, parse error, etc.) we fall back to a
      // generic line that never echoes ``[object Object]`` or a stack.
      let msg: string
      if (err instanceof AuthError && err.status === 403) {
        msg = "You don't have permission to run the agent on this document."
      } else if (err instanceof AuthError && err.status === 401) {
        msg = 'Session expired — sign in again to continue.'
      } else if (err instanceof Error && err.message) {
        msg = err.message
      } else {
        msg = 'Failed to start agent. Please try again.'
      }
      append({
        kind: 'error',
        content: msg,
        at: new Date(),
        retryable: { prompt: text, attachments: sentAttachmentSummaries },
      })
      toast.error(msg)
      setRunning(false)
      // Tagged ``chat.send_failed`` for chat-mode, ``fill.start_failed`` for
      // fill-mode so the issue stream separates the two AI surfaces. AuthError
      // 401/403 are user-state failures (session expired, no permission) and
      // surface as ``warning`` so they don't drown real outages; everything
      // else (5xx, network, parse) is captured as an exception at ``error``.
      const event = mode === 'chat' ? 'chat.send_failed' : 'fill.start_failed'
      const status =
        err instanceof AuthError
          ? err.status
          : err instanceof Error && 'status' in err
            ? (err as { status?: number }).status
            : undefined
      const fields = {
        operation: mode === 'chat' ? 'chat_send' : 'fill_trigger',
        route:
          mode === 'chat'
            ? '/dashboard/documents/{uuid}/chat'
            : '/dashboard/documents/{uuid}/fill',
        mode,
        doc_uuid: doc.uuid,
        status,
      }
      if (err instanceof AuthError) {
        captureSafeMessage(event, 'warning', fields)
      } else {
        captureSafeException(err, event, fields)
      }
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void sendPrompt(input.trim())
  }

  const handleRetry = (prompt: string) => {
    void sendPrompt(prompt)
  }

  const isEmpty = entries.length === 0
  const historySessions = chats.data ?? []
  const canShowHistory =
    isEmpty &&
    showHistory &&
    !running &&
    (chats.isLoading || Boolean(chats.error) || historySessions.length > 0)
  const showTypingIndicator =
    running &&
    (entries.length === 0 ||
      entries[entries.length - 1]?.kind === 'user' ||
      entries[entries.length - 1]?.kind === 'run-start' ||
      entries[entries.length - 1]?.kind === 'assistant')

  const trimmedInput = input.trim()
  const composerDisabled = running || triggerFill.isPending || sendChatMessage.isPending
  const { isDragging, handlers: dropHandlers } = useAttachmentDropTarget({
    onAddFiles: attachments.addFiles,
    disabled: composerDisabled,
  })
  const startNewChat = () => {
    controllerRef.current?.abort()
    controllerRef.current = null
    setEntries([])
    setInput('')
    setRunning(false)
    setChatSessionUuid(null)
    setHistorySessionUuid(null)
    setShowHistory(false)
  }

  return (
    <div
      // The chat surface fills its work-area parent and lays out as
      // transcript ↑ + composer ↓. Both mobile and desktop derive the
      // panel height from the parent flex column (tab-panel-chat) — a
      // previous iteration hardcoded ``md:h-[calc(100vh-11rem)]`` which
      // overshot the parent by ~29px (more once the in-progress
      // FillingBanner mounts), pushing the composer below the viewport
      // and producing the "desktop chat doesn't work" prod report.
      //
      // `paddingBottom: env(keyboard-inset-height)` keeps the composer
      // above an iOS soft keyboard; safe-area-inset-bottom keeps it above
      // the home indicator on notched devices.
      className="flex min-h-[22rem] flex-1 flex-col gap-3 md:min-h-[24rem]"
      style={{
        paddingBottom:
          'max(env(keyboard-inset-height, 0px), env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="chat-panel"
    >
      {readOnly && (
        <div
          role="status"
          data-testid="chat-readonly-banner"
          className="flex items-start gap-2 rounded-md border border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground"
        >
          <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            Read-only — your account can&apos;t trigger agent runs. You can
            still review prior chat history on the Chat tab.
          </span>
        </div>
      )}
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-lg border border-border bg-muted/30 p-3"
        data-testid="chat-message-list"
        role="log"
        aria-label="Agent conversation transcript"
        aria-live="polite"
        aria-relevant="additions"
      >
        {isEmpty ? (
          canShowHistory ? (
            historySessionUuid ? (
              <ChatHistoryTranscript
                sessionUuid={historySessionUuid}
                onBack={() => setHistorySessionUuid(null)}
                onNewChat={startNewChat}
              />
            ) : (
              <ChatHistoryStart
                sessions={historySessions}
                isLoading={chats.isLoading}
                error={chats.error}
                onOpen={setHistorySessionUuid}
                onNewChat={startNewChat}
              />
            )
          ) : (
            <EmptyState
              mode={mode}
              disabled={readOnly || composerDisabled}
              onPick={(prompt) => void sendPrompt(prompt)}
              onShowHistory={
                historySessions.length > 0
                  ? () => {
                      setShowHistory(true)
                      setHistorySessionUuid(null)
                    }
                  : undefined
              }
            />
          )
        ) : (
          <div className="space-y-2">
            {entries.map((entry, i) => (
              <MessageView
                key={i}
                entry={entry}
                allEntries={entries}
                docUuid={doc.uuid}
                onUpdateProposal={updateProposal}
                onRetry={handleRetry}
              />
            ))}
            {showTypingIndicator && <TypingIndicator mode={mode} />}
          </div>
        )}
        <div ref={listEndRef} />
      </div>
      <ChatToolbar
        mode={mode}
        onToggleMode={() => {
          setMode((m) => (m === 'fill' ? 'chat' : 'fill'))
          setChatSessionUuid(null)
        }}
      />
      {mode === 'fill' && (
        <>
          <EffectiveInstructionsPanel
            documentId={doc.uuid}
            savedPromptUuid={selectedPrompt?.uuid ?? null}
            userInstructions={
              trimmedInput.length > 0 && trimmedInput.length <= CHAT_INSTRUCTION_MAX_CHARS
                ? trimmedInput
                : null
            }
            collapsedByDefault
          />
          <div className="flex items-center justify-between gap-2">
            <SavedPromptPicker
              selected={selectedPrompt}
              onSelect={setSelectedPrompt}
              disabled={composerDisabled}
            />
            <span className="text-[11px] text-muted-foreground/80">
              Selected prompts layer into your next fill run.
            </span>
          </div>
        </>
      )}
      <div
        className="relative"
        data-testid="chat-composer-region"
        onDragEnter={dropHandlers.onDragEnter}
        onDragOver={dropHandlers.onDragOver}
        onDragLeave={dropHandlers.onDragLeave}
        onDrop={dropHandlers.onDrop}
      >
        <AttachmentComposer
          attachments={attachments.attachments}
          uploading={attachments.uploading}
          lastValidationError={attachments.lastValidationError}
          onAddFiles={attachments.addFiles}
          onRemove={attachments.remove}
          onRetry={attachments.retry}
          onClearValidationError={attachments.clearValidationError}
          disabled={composerDisabled}
        />
        <ChatInput
          value={input}
          onChange={setInput}
          onSubmit={onSubmit}
          onStop={stop}
          running={running}
          pending={triggerFill.isPending || sendChatMessage.isPending}
          mode={mode}
          readOnly={readOnly}
          onPaste={dropHandlers.onPaste}
        />
        <AttachmentDropOverlay visible={isDragging} />
      </div>
    </div>
  )
}

const CHAT_EXAMPLE_PROMPTS = [
  'What fields does this form have?',
  'Suggest a value for the applicant name field.',
] as const

function EmptyState({
  mode,
  disabled,
  onPick,
  onShowHistory,
}: {
  mode: ChatMode
  disabled: boolean
  onPick: (prompt: string) => void
  onShowHistory?: () => void
}) {
  const isChat = mode === 'chat'
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-3 px-2 text-center"
      data-testid="chat-empty-state"
    >
      <div className="rounded-full bg-primary-soft p-2.5 text-primary-soft-foreground">
        {isChat ? (
          <MessageSquare className="size-5" aria-hidden />
        ) : (
          <Sparkles className="size-5" aria-hidden />
        )}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold tracking-tight">
          {isChat ? 'Chat with the AI' : 'Ask the agent to fill this form'}
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {isChat
            ? 'Proposals stay pending until you accept or reject them. Switch to Fill mode for auto-apply.'
            : 'Proposals appear inline on the Fields tab. Use the buttons below or type your own request.'}
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2 pt-1">
        {(isChat ? CHAT_EXAMPLE_PROMPTS : EXAMPLE_PROMPTS).map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => onPick(prompt)}
            disabled={disabled}
            className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground shadow-soft transition hover:border-primary/50 hover:bg-card hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {prompt}
          </button>
        ))}
      </div>
      {onShowHistory ? (
        <button
          type="button"
          onClick={onShowHistory}
          className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          data-testid="chat-show-history"
        >
          <MessageSquare className="size-3" aria-hidden />
          Show chat history
        </button>
      ) : null}
    </div>
  )
}

function ChatHistoryStart({
  sessions,
  isLoading,
  error,
  onOpen,
  onNewChat,
}: {
  sessions: readonly ChatSessionSummary[]
  isLoading: boolean
  error: unknown
  onOpen: (uuid: string) => void
  onNewChat: () => void
}) {
  return (
    <div className="flex min-h-full flex-col gap-3" data-testid="chat-history-inline">
      <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-3">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold tracking-tight">Chat history</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Continue from a prior document chat or start fresh with the composer
            below.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={onNewChat}
          data-testid="chat-new-chat"
        >
          <Plus className="size-3.5" aria-hidden />
          New chat
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="rounded-md border border-destructive-soft bg-destructive-soft/30 p-3 text-sm text-destructive-soft-foreground">
          {error instanceof Error ? error.message : 'Failed to load chat history.'}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="chat-history-list">
          {sessions.map((session) => (
            <li key={session.uuid}>
              <ChatHistoryRow
                session={session}
                onOpen={() => onOpen(session.uuid)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ChatHistoryRow({
  session,
  onOpen,
}: {
  session: ChatSessionSummary
  onOpen: () => void
}) {
  const label = session.title.trim() || '(no title)'
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-3 rounded-lg border border-border bg-background p-3 text-left transition-colors hover:border-border-strong hover:bg-accent/60"
      data-testid="chat-history-row"
    >
      <span
        aria-hidden
        className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
      >
        <MessageSquare className="size-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {label}
        </span>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <time dateTime={session.created_at}>
            {formatHistoryTime(session.created_at)}
          </time>
          <span>
            {session.message_count} message
            {session.message_count === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      <ChevronRight
        className="mt-0.5 size-4 shrink-0 text-muted-foreground"
        aria-hidden
      />
    </button>
  )
}

function ChatHistoryTranscript({
  sessionUuid,
  onBack,
  onNewChat,
}: {
  sessionUuid: string
  onBack: () => void
  onNewChat: () => void
}) {
  const chat = useChat(sessionUuid)
  const title = chat.data?.title?.trim() || 'Chat transcript'

  return (
    <div className="flex min-h-full flex-col gap-3" data-testid="chat-history-transcript">
      <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-3">
        <div className="min-w-0 space-y-1">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            data-testid="chat-history-back"
          >
            <ArrowLeft className="size-3" aria-hidden />
            Back to history
          </button>
          <p
            className="truncate text-sm font-semibold tracking-tight"
            data-testid="chat-transcript-title"
          >
            {title}
          </p>
          {chat.data ? (
            <p className="text-xs text-muted-foreground">
              {chat.data.document_filename} · {formatHistoryTime(chat.data.created_at)}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onNewChat}
          data-testid="chat-new-chat"
        >
          <Plus className="size-3.5" aria-hidden />
          New chat
        </Button>
      </div>

      {chat.isLoading ? (
        <div className="flex flex-1 items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : chat.error ? (
        <div className="rounded-md border border-destructive-soft bg-destructive-soft/30 p-3 text-sm text-destructive-soft-foreground">
          {chat.error instanceof Error
            ? chat.error.message
            : 'Failed to load chat transcript.'}
        </div>
      ) : chat.data && chat.data.messages.length > 0 ? (
        <div className="space-y-2" data-testid="chat-transcript">
          {chat.data.messages.map((message, index) => (
            <HistoryTranscriptMessage key={index} message={message} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">This chat has no messages.</p>
      )}
    </div>
  )
}

function HistoryTranscriptMessage({
  message,
}: {
  message: {
    role: 'user' | 'assistant' | 'system'
    kind: 'text' | 'field_proposal' | 'status'
    content: string
  }
}) {
  const content =
    message.role === 'assistant'
      ? sanitizeAssistantText(message.content)
      : message.content
  if (content == null) return null
  if (message.role === 'system') {
    return (
      <div className="text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {content}
      </div>
    )
  }
  return (
    <div
      className={cn(
        'flex',
        message.role === 'user' ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={cn(
          'max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm shadow-sm',
          message.role === 'user'
            ? 'rounded-br-md bg-primary text-primary-foreground'
            : 'rounded-bl-md border border-border bg-background text-foreground',
          message.kind === 'field_proposal' &&
            'border border-warning-soft bg-warning-soft text-warning-soft-foreground',
        )}
      >
        {message.kind === 'field_proposal' && (
          <span className="mr-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide">
            <Sparkles className="size-3" /> proposal
          </span>
        )}
        {content}
      </div>
    </div>
  )
}

const HISTORY_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function formatHistoryTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return HISTORY_DATE_FORMAT.format(date)
}

export function MessageView({
  entry,
  allEntries = [],
  debug = false,
  docUuid = '',
  onUpdateProposal,
  onRetry,
}: {
  entry: ChatEntry
  allEntries?: readonly ChatEntry[]
  debug?: boolean
  docUuid?: string
  onUpdateProposal?: (uuid: string, updated: EditProposalRead) => void
  onRetry?: (prompt: string, attachments: readonly SentAttachmentSummary[]) => void
}) {
  switch (entry.kind) {
    case 'user':
      return <UserBubble content={entry.content} at={entry.at} />
    case 'assistant':
      return <AssistantBubble content={entry.content} at={entry.at} />
    case 'tool':
      return (
        <ToolCallBubble
          name={entry.name}
          summary={entry.summary}
          parsed={entry.parsed}
          debug={debug}
        />
      )
    case 'proposal':
      return <ProposalBubble proposal={entry.proposal} debug={debug} at={entry.at} />
    case 'pending-proposal':
      return (
        <PendingProposalBubble
          proposal={entry.proposal}
          docUuid={docUuid}
          debug={debug}
          onUpdate={onUpdateProposal}
        />
      )
    case 'context-used':
      return <ContextUsedChip summary={entry.summary} />
    case 'attachments':
      return <SentAttachmentsBubble attachments={entry.attachments} />
    case 'run-start':
      return <RunStartChip runId={entry.runId} debug={debug} />
    case 'run-done': {
      const runId = lastRunIdBefore(allEntries, entry)
      const counts = runId ? countProposalsForRun(allEntries, runId) : null
      return <RunDoneChip counts={counts} runId={runId} debug={debug} />
    }
    case 'run-stopped':
      return <SystemChip variant="info" content="Stopped" />
    case 'error':
      return (
        <SystemChip
          variant="error"
          content={entry.content}
          retryable={entry.retryable}
          onRetry={onRetry}
        />
      )
  }
}

function lastRunIdBefore(
  entries: readonly ChatEntry[],
  target: ChatEntry,
): string | null {
  const idx = entries.indexOf(target)
  if (idx < 0) return null
  for (let i = idx - 1; i >= 0; i--) {
    const e = entries[i]
    if (e?.kind === 'run-start') return e.runId
  }
  return null
}

function UserBubble({ content, at }: { content: string; at?: Date }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-3 py-2 text-sm text-primary-foreground shadow-sm">
        {content}
      </div>
      <ChatTimestamp at={at} />
    </div>
  )
}

function SentAttachmentsBubble({
  attachments,
}: {
  attachments: readonly SentAttachmentSummary[]
}) {
  if (attachments.length === 0) return null
  return (
    <div className="flex justify-end" data-testid="chat-sent-attachments">
      <div className="max-w-[85%] space-y-1">
        <div className="text-right text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Context sent with message
        </div>
        <ul className="flex flex-wrap justify-end gap-1.5">
          {attachments.map((a) => (
            <li key={a.uuid}>
              <div
                data-testid="chat-sent-attachment-chip"
                className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-[11px] text-foreground shadow-sm"
              >
                <span
                  aria-hidden
                  className="inline-flex size-4 items-center justify-center rounded text-muted-foreground"
                >
                  <SentAttachmentIcon kind={a.kind} />
                </span>
                <span className="max-w-[14rem] truncate font-medium" title={a.filename}>
                  {a.filename}
                </span>
                <span className="text-muted-foreground">
                  {kindLabel(a.kind)} · {formatByteSize(a.byteSize)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function SentAttachmentIcon({ kind }: { kind: AttachmentKind }) {
  if (kind === 'screenshot') return <ScanLine className="size-3" />
  if (kind === 'image') return <ImageIcon className="size-3" />
  if (kind === 'pdf' || kind === 'text') return <FileText className="size-3" />
  return <Paperclip className="size-3" />
}

function AssistantBubble({ content, at }: { content: string; at?: Date }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] space-y-1">
        <div className="flex items-center gap-2">
          <RoleLabel label="Agent" />
          <ChatTimestamp at={at} />
        </div>
        <div className="whitespace-pre-wrap rounded-2xl rounded-bl-md border bg-background px-3 py-2 text-sm shadow-sm">
          {content}
        </div>
      </div>
    </div>
  )
}

function ToolCallBubble({
  name,
  summary,
  parsed,
  debug,
}: {
  name: string | null
  summary: string | null
  parsed: Record<string, unknown>
  debug: boolean
}) {
  const displayName = friendlyToolName(name)
  // In default (non-debug) mode we deliberately do NOT render the raw JSON
  // body or expose a disclosure to it — only a one-line summary. Raw JSON
  // belongs to debug mode, behind the toolbar toggle.
  if (!debug) {
    return (
      <div className="flex justify-start" data-testid="chat-tool-call">
        <div className="inline-flex max-w-[85%] items-center gap-2 rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground shadow-sm">
          <Sparkles className="size-3 shrink-0" aria-hidden />
          <span className="font-medium text-foreground">{displayName}</span>
          {summary ? (
            <span className="min-w-0 flex-1 truncate" title={summary}>
              {summary}
            </span>
          ) : null}
        </div>
      </div>
    )
  }
  return (
    <div className="flex justify-start">
      <details
        className="group max-w-[85%] rounded-2xl rounded-bl-md border bg-muted/40 px-3 py-2 text-sm shadow-sm"
        data-testid="chat-tool-call"
      >
        <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
          <Sparkles className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="font-medium">{displayName}</span>
          {summary && (
            <span
              className="min-w-0 flex-1 truncate text-xs text-muted-foreground"
              title={summary}
            >
              — {summary}
            </span>
          )}
          <ChevronRight
            className="size-3 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
            aria-hidden
          />
        </summary>
        <pre
          className="mt-2 max-h-72 overflow-auto rounded bg-background p-2 text-[11px] leading-relaxed text-muted-foreground"
          data-testid="chat-tool-call-raw"
        >
          {JSON.stringify(parsed, null, 2)}
        </pre>
      </details>
    </div>
  )
}

function friendlyToolName(name: string | null): string {
  if (!name) return 'Looking something up'
  const cleaned = name.replace(/[_-]+/g, ' ').trim()
  if (cleaned.length === 0) return 'Looking something up'
  // Title-case the first letter; leave the rest as-is so acronyms survive.
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

function ProposalBubble({
  proposal,
  debug,
  at,
}: {
  proposal: NormalizedProposal
  debug: boolean
  at?: Date
}) {
  const applied = proposal.applied
  const conf = formatConfidence(proposal.confidence)
  const isLowConfidence =
    proposal.confidence != null && proposal.confidence < LOW_CONFIDENCE_THRESHOLD
  const hasReasoning =
    proposal.reasoning !== null && proposal.reasoning.trim().length > 0
  const citations = proposalCitations(proposal)

  return (
    <div className="flex justify-start">
      <div
        className={cn(
          'w-full max-w-[85%] overflow-hidden rounded-xl border bg-background text-sm shadow-sm',
          applied
            ? 'border-emerald-200 dark:border-emerald-900'
            : 'border-amber-200 dark:border-amber-900',
        )}
        data-testid="chat-proposal"
        data-applied={applied ? 'true' : 'false'}
        data-low-confidence={isLowConfidence ? 'true' : 'false'}
      >
        <div className="flex items-start gap-3 px-3 py-2">
          <StatusDot applied={applied} />
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex items-baseline justify-between gap-2">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span
                  className={cn(
                    'text-[11px] font-medium uppercase tracking-wide',
                    applied
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-amber-700 dark:text-amber-400',
                  )}
                >
                  {applied ? 'Filled' : 'Suggested'}
                </span>
                <span
                  className={cn(
                    'min-w-0 truncate text-sm font-medium text-foreground',
                    !proposal.hasFriendlyName && 'font-mono text-xs',
                  )}
                >
                  {proposal.displayName}
                </span>
              </div>
              <ChatTimestamp at={at} />
            </div>
            <ProposalValue
              value={proposal.displayValue}
              isEmpty={proposal.isEmpty}
            />
            {isLowConfidence && conf ? (
              <div
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                data-testid="chat-proposal-low-confidence"
              >
                Low confidence ({conf})
              </div>
            ) : null}
            {citations.length > 0 ? <CitationChips citations={citations} /> : null}
          </div>
        </div>
        {hasReasoning ? (
          <details className="group/r border-t bg-muted/30">
            <summary
              className="flex cursor-pointer list-none items-center gap-1 px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden"
              data-testid="chat-proposal-why-toggle"
            >
              <ChevronRight
                className="size-3 transition-transform group-open/r:rotate-90"
                aria-hidden
              />
              Why this value?
            </summary>
            <p
              className="whitespace-pre-wrap border-t bg-background px-3 py-2 text-xs text-muted-foreground"
              data-testid="chat-proposal-reasoning"
            >
              {proposal.reasoning}
            </p>
          </details>
        ) : null}
        {debug ? (
          <dl
            className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 border-t bg-background px-3 py-2 text-[11px] text-muted-foreground"
            data-testid="chat-proposal-debug"
          >
            <dt>Field id</dt>
            <dd className="break-all font-mono text-foreground">
              {proposal.rawFieldName}
            </dd>
            <dt>Raw value</dt>
            <dd className="break-all font-mono text-foreground">
              {proposal.value === '' ? '(empty)' : proposal.value}
            </dd>
            {conf ? (
              <>
                <dt>Confidence</dt>
                <dd>{conf}</dd>
              </>
            ) : null}
            <dt>Run</dt>
            <dd className="font-mono">{shortRunId(proposal.runId)}</dd>
          </dl>
        ) : null}
      </div>
    </div>
  )
}

// ── Interactive (chat-mode) pending proposals ────────────────────────────────

function PendingProposalBubble({
  proposal,
  docUuid,
  debug,
  onUpdate,
}: {
  proposal: EditProposalRead
  docUuid: string
  debug: boolean
  onUpdate?: (uuid: string, updated: EditProposalRead) => void
}) {
  const accept = useAcceptProposal(docUuid)
  const reject = useRejectProposal(docUuid)

  const displayName =
    (proposal.friendly_name?.trim() ?? '') || proposal.field_name_hint
  const hasFriendlyName =
    !!proposal.friendly_name?.trim() && proposal.friendly_name.trim().length > 0
  const proposedValue = proposal.proposed_value ?? ''
  const { displayValue, isEmpty } = humanizeValue(proposedValue, proposal.field_kind ?? null)
  const conf = formatConfidence(proposal.confidence ?? null)
  const isLowConfidence =
    proposal.confidence != null && proposal.confidence < LOW_CONFIDENCE_THRESHOLD
  const hasReasoning = !!proposal.reasoning?.trim()

  const isPending = proposal.status === 'pending'
  const isAccepted = proposal.status === 'accepted'
  const isRejected = proposal.status === 'rejected'
  const isSuperseded = proposal.status === 'superseded'

  const onAccept = async () => {
    try {
      const updated = await accept.mutateAsync(proposal.uuid)
      onUpdate?.(proposal.uuid, updated)
      toast.success(`Applied ${displayName}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to accept')
    }
  }

  const onReject = async () => {
    try {
      const updated = await reject.mutateAsync({ proposalUuid: proposal.uuid })
      onUpdate?.(proposal.uuid, updated)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject')
    }
  }

  const borderClass = isAccepted
    ? 'border-emerald-200 dark:border-emerald-900'
    : isRejected || isSuperseded
      ? 'border-border'
      : 'border-blue-200 dark:border-blue-900'

  return (
    <div className="flex justify-start">
      <div
        className={cn(
          'w-full max-w-[85%] overflow-hidden rounded-xl border bg-background text-sm shadow-sm',
          borderClass,
          (isRejected || isSuperseded) && 'opacity-60',
        )}
        data-testid="chat-pending-proposal"
        data-status={proposal.status}
      >
        <div className="flex items-start gap-3 px-3 py-2">
          <PendingProposalStatusDot status={proposal.status} />
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span
                className={cn(
                  'text-[11px] font-medium uppercase tracking-wide',
                  isAccepted
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : isRejected
                      ? 'text-muted-foreground'
                      : isSuperseded
                        ? 'text-muted-foreground'
                        : 'text-blue-700 dark:text-blue-400',
                )}
              >
                {isAccepted
                  ? 'Accepted'
                  : isRejected
                    ? 'Rejected'
                    : isSuperseded
                      ? 'Superseded'
                      : 'Proposed'}
              </span>
              <span
                className={cn(
                  'min-w-0 truncate text-sm font-medium text-foreground',
                  !hasFriendlyName && 'font-mono text-xs',
                )}
              >
                {displayName}
              </span>
            </div>
            <ProposalValue value={displayValue} isEmpty={isEmpty} />
            {isLowConfidence && conf ? (
              <div
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                data-testid="chat-proposal-low-confidence"
              >
                Low confidence ({conf})
              </div>
            ) : null}
          </div>
          {isPending && (
            <div className="flex shrink-0 items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1 px-2 text-xs"
                onClick={() => void onAccept()}
                disabled={accept.isPending || reject.isPending}
                data-testid="chat-proposal-accept"
                aria-label={`Accept proposal for ${displayName}`}
              >
                {accept.isPending ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Check className="size-3 text-emerald-600" />
                )}
                Apply
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-destructive"
                onClick={() => void onReject()}
                disabled={accept.isPending || reject.isPending}
                data-testid="chat-proposal-reject"
                aria-label={`Reject proposal for ${displayName}`}
              >
                {reject.isPending ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <X className="size-3" />
                )}
                Dismiss
              </Button>
            </div>
          )}
        </div>
        {hasReasoning ? (
          <details className="group/r border-t bg-muted/30">
            <summary
              className="flex cursor-pointer list-none items-center gap-1 px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden"
              data-testid="chat-proposal-why-toggle"
            >
              <ChevronRight
                className="size-3 transition-transform group-open/r:rotate-90"
                aria-hidden
              />
              Why this value?
            </summary>
            <p
              className="whitespace-pre-wrap border-t bg-background px-3 py-2 text-xs text-muted-foreground"
              data-testid="chat-proposal-reasoning"
            >
              {proposal.reasoning}
            </p>
          </details>
        ) : null}
        {debug ? (
          <dl
            className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 border-t bg-background px-3 py-2 text-[11px] text-muted-foreground"
            data-testid="chat-pending-proposal-debug"
          >
            <dt>UUID</dt>
            <dd className="break-all font-mono text-foreground">{proposal.uuid}</dd>
            <dt>Field hint</dt>
            <dd className="break-all font-mono text-foreground">{proposal.field_name_hint}</dd>
            <dt>Status</dt>
            <dd>{proposal.status}</dd>
            {conf ? (
              <>
                <dt>Confidence</dt>
                <dd>{conf}</dd>
              </>
            ) : null}
          </dl>
        ) : null}
      </div>
    </div>
  )
}

function PendingProposalStatusDot({ status }: { status: string }) {
  if (status === 'accepted') {
    return (
      <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
        <CheckCircle2 className="size-3.5" aria-hidden />
      </span>
    )
  }
  if (status === 'rejected' || status === 'superseded') {
    return (
      <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <X className="size-3" aria-hidden />
      </span>
    )
  }
  return (
    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">
      <Sparkles className="size-3.5" aria-hidden />
    </span>
  )
}

// ── Context-used chip ────────────────────────────────────────────────────────

function ContextUsedChip({ summary }: { summary: ChatContextSummary }) {
  const parts: string[] = []
  if (summary.selected_field_count > 0) {
    parts.push(
      `${summary.selected_field_count} of ${summary.total_field_count} fields`,
    )
  } else if (summary.total_field_count > 0) {
    parts.push(`${summary.total_field_count} fields`)
  }
  if (summary.attachment_count > 0) {
    parts.push(
      `${summary.attachment_count} attachment${summary.attachment_count === 1 ? '' : 's'}`,
    )
  }
  if (summary.has_initial_context) {
    parts.push('document context')
  }

  if (parts.length === 0) return null

  return (
    <div className="flex justify-start" data-testid="chat-context-used">
      <div className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-2.5 py-1 text-[10px] text-muted-foreground">
        <Zap className="size-3 shrink-0" aria-hidden />
        <span>Context: {parts.join(' · ')}</span>
      </div>
    </div>
  )
}

function StatusDot({ applied }: { applied: boolean }) {
  return (
    <span
      className={cn(
        'mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full',
        applied
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
          : 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300',
      )}
      aria-hidden
    >
      {applied ? (
        <CheckCircle2 className="size-3.5" />
      ) : (
        <Sparkles className="size-3.5" />
      )}
    </span>
  )
}

function ProposalValue({
  value,
  isEmpty,
}: {
  value: string
  isEmpty: boolean
}) {
  if (isEmpty) {
    return (
      <p className="text-sm italic text-muted-foreground" data-testid="chat-proposal-value">
        {value}
      </p>
    )
  }
  return (
    <p
      className="break-words text-sm text-foreground"
      data-testid="chat-proposal-value"
    >
      {value}
    </p>
  )
}

function CitationChips({ citations }: { citations: CitationSource[] }) {
  if (citations.length === 0) return null
  return (
    <div className="mt-1.5 flex flex-wrap gap-1" data-testid="chat-citation-chips">
      {citations.map((c, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground"
          title={c.detail ?? undefined}
          data-testid="chat-citation-chip"
        >
          <CitationIcon kind={c.kind} />
          <span>{c.label}</span>
          {c.detail ? <span className="opacity-60">· {c.detail}</span> : null}
        </span>
      ))}
    </div>
  )
}

function CitationIcon({ kind }: { kind: CitationSource['kind'] }) {
  if (kind === 'field') return <FileText className="size-2.5 shrink-0" aria-hidden />
  if (kind === 'attachment') return <Paperclip className="size-2.5 shrink-0" aria-hidden />
  return <ScanLine className="size-2.5 shrink-0" aria-hidden />
}

function RunStartChip({ runId, debug }: { runId: string; debug: boolean }) {
  return (
    <div className="flex justify-center" data-testid="chat-run-start">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        <Sparkles className="size-3 shrink-0" aria-hidden />
        <span>Filling form</span>
        {debug ? (
          <span className="font-mono normal-case" data-testid="chat-run-id">
            #{shortRunId(runId)}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function RunDoneChip({
  counts,
  runId,
  debug,
}: {
  counts: { filled: number; suggested: number } | null
  runId: string | null
  debug: boolean
}) {
  const parts: string[] = []
  if (counts) {
    if (counts.filled > 0) {
      parts.push(`Filled ${counts.filled} field${counts.filled === 1 ? '' : 's'}`)
    }
    if (counts.suggested > 0) {
      parts.push(
        `Suggested ${counts.suggested} field${counts.suggested === 1 ? '' : 's'}`,
      )
    }
  }
  const label = parts.length > 0 ? parts.join(' • ') : 'Finished'
  return (
    <div className="flex justify-center" data-testid="chat-run-done">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100/60 px-3 py-0.5 text-[10px] uppercase tracking-wide text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
        <CheckCircle2 className="size-3 shrink-0" aria-hidden />
        <span>{label}</span>
        {debug && runId ? (
          <span className="font-mono normal-case" data-testid="chat-run-id">
            #{shortRunId(runId)}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function SystemChip({
  variant,
  content,
  retryable,
  onRetry,
}: {
  variant: 'info' | 'error'
  content: string
  retryable?: { prompt: string; attachments: readonly SentAttachmentSummary[] }
  onRetry?: (prompt: string, attachments: readonly SentAttachmentSummary[]) => void
}) {
  if (variant === 'error') {
    return (
      <div className="flex justify-center">
        <div className="inline-flex max-w-[90%] items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1 text-xs text-destructive">
          <AlertCircle className="size-3 shrink-0" aria-hidden />
          <span className="truncate" title={content}>
            {content}
          </span>
          {retryable && onRetry ? (
            <button
              type="button"
              onClick={() => onRetry(retryable.prompt, retryable.attachments)}
              data-testid="chat-error-retry"
              className="ml-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-destructive/30 px-2 py-0.5 text-[10px] hover:bg-destructive/20"
            >
              <RefreshCw className="size-2.5" aria-hidden />
              Retry
            </button>
          ) : null}
        </div>
      </div>
    )
  }
  return (
    <div className="flex justify-center">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        <span>{content}</span>
      </div>
    </div>
  )
}

function RoleLabel({ label }: { label: string }) {
  return (
    <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
  )
}

function ChatTimestamp({ at }: { at?: Date }) {
  if (!at) return null
  const short = formatChatTimestamp(at)
  if (!short) return null
  const full = fullChatTimestamp(at)
  return (
    <time
      dateTime={at.toISOString()}
      title={full ?? undefined}
      className="shrink-0 text-[10px] text-muted-foreground"
      data-testid="chat-timestamp"
    >
      {short}
    </time>
  )
}

function TypingIndicator({ mode = 'fill' }: { mode?: ChatMode }) {
  const label = mode === 'chat' ? 'AI is thinking' : 'Agent is thinking'
  return (
    <div className="flex justify-start" aria-live="polite" aria-label={label}>
      <div className="inline-flex items-center gap-1 rounded-2xl rounded-bl-md border bg-background px-3 py-2 text-muted-foreground shadow-sm">
        <Dot delay="0ms" />
        <Dot delay="120ms" />
        <Dot delay="240ms" />
      </div>
    </div>
  )
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="inline-block size-1.5 animate-bounce rounded-full bg-current"
      style={{ animationDelay: delay }}
    />
  )
}

function ChatToolbar({
  mode,
  onToggleMode,
}: {
  mode: ChatMode
  onToggleMode: () => void
}) {
  return (
    <div className="flex items-center justify-start">
      <div className="flex items-center gap-1 rounded-full border border-border bg-muted/40 p-0.5" data-testid="chat-mode-toggle">
        <button
          type="button"
          onClick={() => mode !== 'fill' && onToggleMode()}
          data-testid="chat-mode-fill"
          aria-pressed={mode === 'fill'}
          className={cn(
            'inline-flex h-5 items-center gap-1 rounded-full px-2 text-[11px] font-medium transition-colors',
            mode === 'fill'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Zap className="size-2.5" aria-hidden />
          Fill
        </button>
        <button
          type="button"
          onClick={() => mode !== 'chat' && onToggleMode()}
          data-testid="chat-mode-chat"
          aria-pressed={mode === 'chat'}
          className={cn(
            'inline-flex h-5 items-center gap-1 rounded-full px-2 text-[11px] font-medium transition-colors',
            mode === 'chat'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <MessageSquare className="size-2.5" aria-hidden />
          Chat
        </button>
      </div>
    </div>
  )
}

function ChatInput({
  value,
  onChange,
  onSubmit,
  onStop,
  running,
  pending,
  mode = 'fill',
  readOnly = false,
  onPaste,
}: {
  value: string
  onChange: (next: string) => void
  onSubmit: (e: React.FormEvent) => void
  onStop: () => void
  running: boolean
  pending: boolean
  mode?: ChatMode
  readOnly?: boolean
  onPaste?: (e: React.ClipboardEvent<HTMLElement>) => void
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const submitRef = useRef<HTMLButtonElement | null>(null)

  // Auto-grow up to ~6 lines. Reset every render so shrinking on backspace
  // also works (otherwise scrollHeight stays at the prior maximum).
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`
  }, [value])

  const placeholder = useMemo(() => {
    if (readOnly) return 'Read-only — chat is disabled.'
    if (pending) return 'Sending…'
    if (running) return mode === 'chat' ? 'AI is thinking…' : 'Agent is running…'
    if (mode === 'chat') return 'Ask a question or request field suggestions — Enter to send'
    return 'Tell the agent what to fill — Enter to send, Shift+Enter for newline'
  }, [pending, running, mode, readOnly])

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.nativeEvent.isComposing
    ) {
      e.preventDefault()
      submitRef.current?.click()
    }
  }

  const trimmed = value.trim()
  const canSend = !readOnly && trimmed.length > 0 && !running && !pending

  return (
    <form
      onSubmit={onSubmit}
      aria-label="Message the fill agent"
      className={cn(
        'sticky bottom-0 flex items-end gap-2 rounded-lg border border-border bg-card/95 p-2 shadow-soft backdrop-blur supports-[backdrop-filter]:bg-card/80',
        'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/30',
      )}
    >
      <label htmlFor="chat-instructions" className="sr-only">
        Tell the agent what to fill
      </label>
      <textarea
        ref={textareaRef}
        id="chat-instructions"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={placeholder}
        aria-label="Tell the agent what to fill"
        rows={1}
        disabled={readOnly || pending}
        data-testid="chat-input"
        className={cn(
          'min-h-[2.25rem] w-full resize-none bg-transparent px-1 py-1.5 text-sm outline-none placeholder:text-muted-foreground',
          'disabled:cursor-not-allowed disabled:opacity-60',
        )}
      />
      {running ? (
        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={onStop}
          data-testid="chat-stop"
          aria-label="Stop agent"
        >
          <Square className="size-4" />
        </Button>
      ) : (
        <Button
          ref={submitRef}
          type="submit"
          size="icon"
          disabled={!canSend}
          data-testid="chat-submit"
          aria-label="Send message"
        >
          {pending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Send className="size-4" />
          )}
        </Button>
      )}
    </form>
  )
}
